'use client';

import type { RumPlugin } from '@datadog/browser-rum';
import { useEffect } from 'react';
import { getCorrelationId } from 'src/helper/correlation-id';
import { REDACTED_RESPONSE_BODY, scrubUrl, scrubUrlsInText } from 'src/helper/telemetry-scrub';

export function DatadogInit() {
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID) {
      Promise.all([
        import('@datadog/browser-rum'),
        import('@datadog/browser-rum-react'),
        import('@datadog/browser-logs'),
      ]).then(([{ datadogRum }, { reactPlugin }, { datadogLogs }]) => {
        const isExtensionStack = (stack: string) =>
          stack.includes('injected-scripts/') ||
          stack.includes('chrome-extension://') ||
          stack.includes('moz-extension://') ||
          stack.includes('safari-extension://') ||
          stack.includes('safari-web-extension://');

        // Kaspersky injects a stackless string rejection: "Object Not Found Matching id:..."
        const isExtensionMessage = (message: string) =>
          message.includes('Object Not Found Matching id:');

        // Own-origin requests (RSC payload fetches on client navigation) carry no
        // trace id today, so a slow one cannot be attributed to a call site (APX-425).
        // Matched by exact origin rather than a subdomain wildcard on purpose: sibling
        // hosts the browser genuinely fetches — assets.* (PMTiles range requests via
        // MapLibre), s.*, vendors.* — must NOT receive tracing headers, which are
        // non-simple and would force a CORS preflight. Trailing '/' pins the origin
        // boundary so a look-alike host (appOrigin + '.example.com') cannot match.
        // The SDK normalizes every traced URL to an absolute href before matching,
        // and this file is 'use client' with the read inside useEffect, so window
        // is always defined here.
        const appOrigin = window.location.origin;
        const isOwnOriginUrl = (url: string) => url.startsWith(`${appOrigin}/`);

        datadogRum.init({
          applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID ?? '',
          clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || '',
          site: process.env.NEXT_PUBLIC_DATADOG_SITE || 'datadoghq.com',
          service: process.env.NEXT_PUBLIC_DATADOG_SERVICE || 'foresight-fe',
          env: process.env.NEXT_PUBLIC_PLATFORM || 'production',
          version: process.env.NEXT_PUBLIC_APP_VERSION,
          allowedTracingUrls: [
            /^https:\/\/api\.foresightapp\.ai/,
            /^https:\/\/api\.foresightapp-development\.ai/,
            /^https:\/\/api\.foresightapp-qa\.ai/,
            /^https:\/\/api\.foresightapp-staging\.ai/,
            /^https:\/\/api\.foresightapp-demo\.ai/,
            isOwnOriginUrl,
          ],
          sessionSampleRate:
            process.env.NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE !== undefined
              ? Number(process.env.NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE)
              : 100,
          sessionReplaySampleRate:
            process.env.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE !== undefined
              ? Number(process.env.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE)
              : 20,
          // INP requires interaction + long-task tracking. Default ON so INP,
          // LCP and TTFB are measurable for every session (APX-460); set the
          // env var to 'false' to disable if RUM data volume/cost demands it.
          trackUserInteractions:
            process.env.NEXT_PUBLIC_DATADOG_TRACK_USER_INTERACTIONS !== 'false',
          trackResources: process.env.NEXT_PUBLIC_DATADOG_TRACK_RESOURCES === 'true',
          trackLongTasks: process.env.NEXT_PUBLIC_DATADOG_TRACK_LONG_TASKS !== 'false',
          // FAIL-CLOSED (APX-672). Every rendered string is censored unless an
          // ancestor opts it back in via `src/helper/telemetry-privacy.ts`. This
          // is the ONLY lever that reaches session replay: `beforeSend` below
          // sees RUM *events*, never replay segments, which are serialized on a
          // separate pipeline and masked here at `getTextContent` time.
          //
          // HARDCODED ON PURPOSE — do not make this an env var. The SDK declares
          // this field `strict: false` (browser-rum-core configuration.js), so an
          // unset or empty value falls back to the default with NO error, and a
          // typo logs once and then falls back. The default is 'mask-user-input',
          // i.e. exactly the leaky level this replaced. A per-environment var
          // would therefore fail open, silently, in whichever environment someone
          // forgot to set — and would publish our masking posture in the client
          // bundle besides.
          //
          // 'mask' rather than 'mask-unless-allowlisted': the latter's allowlist
          // is `window.$DD_ALLOW`, a global Set of literal lowercased UI strings
          // with no SDK API to populate it. Datadog sources it from an i18n
          // message bundle; this app has none. Left unpopulated it masks exactly
          // as much as 'mask' while degrading every action name to "" instead of
          // a self-describing placeholder.
          defaultPrivacyLevel: 'mask',
          // Load-bearing as of APX-672 (it was a no-op before, and is the SDK
          // default either way). The click-action tracker seeds its privacy
          // computation with `defaultPrivacyLevel` — trackClickActions.js does
          // `getNodePrivacyLevel(target, configuration.defaultPrivacyLevel)` only
          // when this flag is on — and `getActionNameFromElement` then
          // short-circuits to the literal "Masked Element" for anything at MASK.
          // That is what stops rendered names reaching `action.target.name`;
          // previously a click on the resident dropdown shipped the real name
          // verbatim, because 'mask-user-input' rejects only form elements and a
          // <button> is not one.
          //
          // Three escape hatches survive the flip, in the SDK's priority order:
          //   1. `data-dd-action-name` on the element or ANY ancestor is read
          //      BEFORE the privacy check — it keeps action analytics on an
          //      in-content control WITHOUT unmasking that control in replay.
          //   2. A `dd-privacy-allow` ancestor resolves to ALLOW, so unmasked
          //      chrome keeps its real action name.
          //   3. `data-testid` is a Datadog STABLE_ATTRIBUTE and is never masked,
          //      so `action.target.selector` stays resolvable everywhere.
          //
          // Deliberately NOT scrubbed in beforeSend: blanket-redacting action
          // names would destroy action analytics, and beforeSend cannot reach
          // session replay at all (APX-391 scope note).
          enablePrivacyForActionName: true,
          plugins: [reactPlugin() as RumPlugin],
          // Drop errors injected by browser extensions — they are not app errors
          // and pollute the RUM error dashboard. Runs before any scrubbing so
          // the extension filter still sees the original `chrome-extension://`
          // scheme it matches on.
          beforeSend: (event) => {
            if (
              event.type === 'error' &&
              (isExtensionStack(event.error?.stack ?? '') ||
                isExtensionMessage(event.error?.message ?? ''))
            ) {
              return false;
            }

            // RUM hands this callback a deep clone and afterwards copies back
            // ONLY its declared modifiable paths, and only when the new value is
            // still a string (SDK limitModification). So: mutate in place,
            // always assign a string, and always return true. Returning a new
            // object or assigning undefined leaves the original value in the
            // event — the scrub would read as correct and leak anyway.
            // Every field is guarded even where the SDK types it as required: a
            // beforeSend that throws drops the event and takes telemetry down
            // silently, which is a worse failure than an unscrubbed field.
            if (event.view) {
              event.view.url = scrubUrl(event.view.url, { normalizeRoute: true });
              if (event.view.referrer) {
                event.view.referrer = scrubUrl(event.view.referrer, { normalizeRoute: true });
              }
            }

            if (event.type === 'resource' && event.resource) {
              event.resource.url = scrubUrl(event.resource.url);
            }

            if (event.type === 'error' && event.error) {
              if (event.error.message) {
                event.error.message = scrubUrlsInText(event.error.message);
              }
              if (event.error.stack) {
                event.error.stack = scrubUrlsInText(event.error.stack);
              }
              if (event.error.resource) {
                event.error.resource.url = scrubUrl(event.error.resource.url);
              }
            }

            return true;
          },
        });

        datadogLogs.init({
          clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || '',
          site: process.env.NEXT_PUBLIC_DATADOG_SITE || 'datadoghq.com',
          service: process.env.NEXT_PUBLIC_DATADOG_SERVICE || 'foresight-fe',
          env: process.env.NEXT_PUBLIC_PLATFORM || 'production',
          forwardErrorsToLogs: true,
          sessionSampleRate: 100,
          beforeSend: (log) => {
            if (isExtensionStack(log.error?.stack ?? '')) {
              return false;
            }
            if (
              isExtensionMessage(log.error?.message ?? '') ||
              isExtensionMessage(log.message ?? '')
            ) {
              return false;
            }

            // Unlike RUM, this seam has no limitModification wrapper — the event
            // is mutated directly. It is also the ONLY seam that sees the SDK's
            // own network-error forwarding, which ships a failed request's URL
            // in both `message` and `http.url` with no application code
            // involved, and stamps `view.url` onto every log line from anywhere
            // in the app. Fixing the logger.* call sites cannot reach either.
            if (log.view) {
              log.view.url = scrubUrl(log.view.url, { normalizeRoute: true });
              if (log.view.referrer) {
                log.view.referrer = scrubUrl(log.view.referrer, { normalizeRoute: true });
              }
            }

            if (log.message) {
              log.message = scrubUrlsInText(log.message);
            }

            if (log.http?.url) {
              log.http.url = scrubUrl(log.http.url);
            }

            // The logger.* call sites pass { url } as message context, which
            // lands at the top level of the event rather than under `http`.
            const logContext: Record<string, unknown> = log;
            if (typeof logContext.url === 'string') {
              logContext.url = scrubUrl(logContext.url);
            }

            // `origin: 'network'` covers TWO SDK branches, and only one of them
            // puts a response body in `stack`:
            //   - isServerError(status) — status >= 500 — sets `stack` to the
            //     captured response body, which for this app is a raw API
            //     payload, i.e. resident data. Replace it wholesale.
            //   - isRejected(request) — status === 0: offline, DNS failure, CORS
            //     block, TLS failure — sets `stack` to a REAL V8 stack trace.
            //     Blanket-replacing that would make every failed request in the
            //     product log identically, with no call site and no error kind.
            // `http.status_code` is what tells them apart inside beforeSend.
            const isCapturedResponseBody =
              log.origin === 'network' && Number(log.http?.status_code) >= 500;

            if (log.error?.stack) {
              log.error.stack = isCapturedResponseBody
                ? REDACTED_RESPONSE_BODY
                : scrubUrlsInText(log.error.stack);
            }

            if (log.error?.message) {
              log.error.message = scrubUrlsInText(log.error.message);
            }

            return true;
          },
        });

        const correlationId = getCorrelationId();
        datadogRum.setGlobalContextProperty('correlation_id', correlationId);
        datadogLogs.setGlobalContextProperty('correlation_id', correlationId);
      });
    }
  }, []);

  return null;
}
